import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { loadRoles } from '../features/roles/rolesSlice';
import { loadUsers } from '../features/users/usersSlice';
import {
  fetchPermissionRegistry,
  fetchRolePermissions,
  saveRolePermissions,
  fetchUserOverrides,
  saveUserOverrides,
  PermissionDefinition,
} from '../features/platformPermissions/platformPermissionsApi';
import type { PlatformAccessLevel } from '../features/platformPermissions/platformAccessLevel';

const LEVEL_OPTIONS: { value: PlatformAccessLevel; label: string }[] = [
  { value: 'none', label: 'Sin acceso' },
  { value: 'view', label: 'Solo ver' },
  { value: 'edit', label: 'Editar' },
];

const selectClass =
  'rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring/30';

export function PlatformPermissionsPage() {
  const dispatch = useAppDispatch();
  const { items: roles } = useSelector((s: RootState) => s.roles);
  const { items: users } = useSelector((s: RootState) => s.users);
  const [defs, setDefs] = useState<PermissionDefinition[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [rolePerms, setRolePerms] = useState<Record<string, PlatformAccessLevel>>({});
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userOverrides, setUserOverrides] = useState<Record<string, PlatformAccessLevel | null>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    dispatch(loadRoles({ pageSize: 200 }));
    dispatch(loadUsers({ pageSize: 300 }));
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        setDefs(await fetchPermissionRegistry());
      } catch {
        setDefs([]);
      }
    })();
  }, []);

  const loadRole = useCallback(async (name: string) => {
    if (!name) return;
    setLoading(true);
    setMessage('');
    try {
      setRolePerms(await fetchRolePermissions(name));
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserOv = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoading(true);
    setMessage('');
    try {
      setUserOverrides(await fetchUserOverrides(parseInt(userId, 10)));
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRole) loadRole(selectedRole);
  }, [selectedRole, loadRole]);

  useEffect(() => {
    if (selectedUserId) loadUserOv(selectedUserId);
  }, [selectedUserId, loadUserOv]);

  const saveRole = async () => {
    if (!selectedRole) return;
    setLoading(true);
    setMessage('');
    try {
      const next = await saveRolePermissions(selectedRole, rolePerms);
      setRolePerms(next);
      setMessage('Permisos de rol guardados');
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const saveUser = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    setMessage('');
    try {
      const next = await saveUserOverrides(parseInt(selectedUserId, 10), userOverrides);
      setUserOverrides(next);
      setMessage('Overrides de usuario guardados');
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-10 pb-8">
      <header className="space-y-2 border-b border-border/80 pb-8">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administración</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Permisos granulares</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Por cada funcionalidad elige sin acceso (oculta menú y bloquea API), solo ver o editar (incluye crear).
          Los overrides por usuario sustituyen lo definido en el rol; «Heredar» elimina el override.
        </p>
      </header>

      {message && (
        <div
          role="status"
          className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
        >
          {message}
        </div>
      )}

      <section className="rounded-2xl border border-border/80 bg-card/50 p-6 shadow-sm backdrop-blur-sm sm:p-8">
        <h2 className="text-lg font-semibold text-foreground">Plantilla por rol</h2>
        <p className="mt-1 text-sm text-muted-foreground">Aplica a todos los usuarios con ese rol salvo override individual.</p>
        <div className="mt-6 space-y-4">
          <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Rol</label>
          <select
            className={`${selectClass} w-full max-w-md`}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            <option value="">Selecciona un rol…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.name}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        {selectedRole && (
          <div className="mt-6 space-y-3 rounded-xl border border-border/60 bg-background/80 p-4 max-h-[28rem] overflow-y-auto">
            {defs.map((d) => (
              <div
                key={d.key}
                className="flex flex-col gap-3 border-b border-border/50 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-medium text-foreground">{d.label}</p>
                  <p className="text-xs leading-snug text-muted-foreground">{d.description}</p>
                  <code className="text-[10px] text-muted-foreground/80">{d.key}</code>
                </div>
                <select
                  className={`${selectClass} w-full shrink-0 sm:w-44`}
                  value={rolePerms[d.key] ?? 'none'}
                  onChange={(e) =>
                    setRolePerms((prev) => ({
                      ...prev,
                      [d.key]: e.target.value as PlatformAccessLevel,
                    }))
                  }
                  disabled={loading}
                >
                  {LEVEL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          disabled={!selectedRole || loading}
          onClick={saveRole}
          className="mt-6 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-opacity disabled:opacity-50"
        >
          Guardar rol
        </button>
      </section>

      <section className="rounded-2xl border border-border/80 bg-card/50 p-6 shadow-sm backdrop-blur-sm sm:p-8">
        <h2 className="text-lg font-semibold text-foreground">Overrides por usuario</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Prioridad sobre el rol. «Heredar» deja solo la plantilla del rol.
        </p>
        <div className="mt-6 space-y-4">
          <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Usuario</label>
          <select
            className={`${selectClass} w-full max-w-md`}
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <option value="">Selecciona un usuario…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email} ({u.role})
              </option>
            ))}
          </select>
        </div>
        {selectedUserId && (
          <div className="mt-6 space-y-3 rounded-xl border border-border/60 bg-background/80 p-4 max-h-[28rem] overflow-y-auto">
            {defs.map((d) => {
              const v = userOverrides[d.key];
              return (
                <div
                  key={d.key}
                  className="flex flex-col gap-3 border-b border-border/50 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="font-medium text-foreground">{d.label}</p>
                    <p className="text-xs text-muted-foreground">{d.description}</p>
                  </div>
                  <select
                    className={`${selectClass} w-full shrink-0 sm:w-44`}
                    value={v === null || v === undefined ? 'inherit' : v}
                    onChange={(e) => {
                      const val = e.target.value;
                      setUserOverrides((prev) => ({
                        ...prev,
                        [d.key]: val === 'inherit' ? null : (val as PlatformAccessLevel),
                      }));
                    }}
                    disabled={loading}
                  >
                    <option value="inherit">Heredar del rol</option>
                    {LEVEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
        <button
          type="button"
          disabled={!selectedUserId || loading}
          onClick={saveUser}
          className="mt-6 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-opacity disabled:opacity-50"
        >
          Guardar usuario
        </button>
      </section>
    </div>
  );
}
